<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  //import PopupForm from './UserPhaseForm.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import DateSelectModal from '$lib/DateSelectModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import SubjectActivityEditor from './schedule/SubjectActivityEditor.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { studyProperties } from '$stores/configStore';
  import Toolbar from '$lib/Toolbar.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faPencil,
    faRotate,
    faEye,
    faEyeSlash,
    faCalendarPlus,
    faHistory,
    faTrashCan,
    faFlag,
    faTimer,
    faMicrochip,
    faPlus,
    faTimes,
    faPlay,
    faCheck,
    faStop,
    faFileImport,
  } from '@fortawesome/pro-light-svg-icons';

  // Use proxy API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  import { formatToLocalDate, formatToLocalDateTime } from '$utils/dateUtils.js';
  import Footer from '../lib/Footer.svelte';

  let phases = [];
  let show = false;
  let showForm = false;
  let showConfirmation = false;
  let showStartConfirmation = false;
  let showCompleteConfirmation = false;
  let showStartMeasurementConfirmation = false;
  let showAuditLog = false;
  let editPhase = null;
  let phaseToDelete = null;
  let activityToStart = null;
  let activityToComplete = null;
  let measurementActivityToStart = null;
  let auditLogPhase = null;
  let phaseId = null;
  let showMeasurements = true;
  let isLoading = true;
  let isGenerating = false;
  let generationMessage = '';
  let showGenerationMessage = false;
  let showDateModal = false;
  let startDate = new Date();
  let showActivityEditor = false;
  let selectedActivity = null;
  let subjectSchedule = null;
  let publishedScheduleVersion = null;

  // Add activity modal state
  let showAddActivityModal = false;
  let measurementActivities = [];
  let isLoadingActivities = false;
  let publishedSchedule = null;
  let statusOptions = [];

  // Import state
  let fileInput;
  let currentPhaseForImport = null;
  let isImporting = false;
  let importProgress = '';

  // SMBG feature flag
  let isSMBGEnabled = true;

  // Computed property to check if there are started milestone activities
  $: hasStartedMilestones = phases.some(
    phase => phase.typeCode === 'MILE' && phase.statusLabel === 'Started'
  );

  // Add computed property for audit log permissions
  $: canViewAuditLogs = hasAccessUiAction(
    'schedule.viewAuditLogs',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canStartStopSchedule = hasAccessUiAction(
    'schedule.startStopPhases',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canEditSchedule = hasAccessUiAction(
    'schedule.editPhases',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canCompleteMeasurements = hasAccessUiAction(
    'schedule.completeMeasurements',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canAddMeasurementActivity = hasAccessUiAction(
    'schedule.addMeasurementActivity',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canUseMeasurementAdminTools = hasAccessUiAction(
    'schedule.measurementAdminTools',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Reactive statement to update SMBG feature flag
  $: if ($studyProperties) {
    isSMBGEnabled = $studyProperties.features?.smbg === true;
  }

  onMount(async () => {
    //selectedStudy.set(null);
    //selectedSite.set(null);
    //selectedParticipant.set(null);

    if ($selectedParticipant) {
      fetchSubjectSchedule();
      fetchUserPhases();
    }

    if ($selectedStudy) {
      fetchPublishedScheduleVersion();
      fetchPublishedSchedule();
    }

    // Load status options for start/complete functionality
    loadStatusOptions();
  });

  // Reactive statement to refetch published schedule when study changes
  $: if ($selectedStudy && $selectedStudy._id) {
    fetchPublishedSchedule();
  }

  async function fetchSubjectSchedule() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/subjectSchedule/subject-schedules?subjectGUID=${$selectedParticipant._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch subject schedule');
      }

      const data = await res.json();

      if (data.data && data.data.length > 0) {
        subjectSchedule = data.data[0];
      }
    } catch (err) {
      console.error('Error fetching subject schedule:', err);
    }
  }

  async function fetchUserPhases() {
    try {
      isLoading = true;
      const res = await fetch(
        `${API_BASE_URL}/subjectSchedule/subject-activities?subjectGUID=${$selectedParticipant._id}&pageSize=999`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch subject activities');
      }

      const response = await res.json();

      // Handle paginated response and map field names
      const activities = Array.isArray(response.data) ? response.data : [];

      // Map field names to match what the UI expects
      const mappedActivities = activities.map((activity) => ({
        ...activity,
        typeCode: activity.activityTypeCode,
        typeLabel: activity.activityTypeName,
        statusLabel: activity.statusName,
        // Keep readingCount as is since it's already a direct field
      }));

      // Organize activities in hierarchical structure
      const milestones = mappedActivities
        .filter((activity) => activity.typeCode === 'MILE')
        .sort((a, b) => {
          // Sort milestones by planned date, then by order
          if (a.planned && b.planned) {
            return new Date(a.planned) - new Date(b.planned);
          }
          return (a.order || 0) - (b.order || 0);
        });

      const measurements = mappedActivities.filter(
        (activity) => activity.typeCode === 'MEAS'
      );

      // Build hierarchical structure
      const hierarchicalPhases = [];

      // Add milestones first
      milestones.forEach((milestone) => {
        hierarchicalPhases.push(milestone);

        // Find measurements that have this milestone as parent
        const childMeasurements = measurements
          .filter((measurement) => measurement.parentGUID === milestone._id)
          .sort((a, b) => {
            // Sort by started date first (started activities first)
            const aStarted = a.started ? new Date(a.started) : null;
            const bStarted = b.started ? new Date(b.started) : null;

            // If both have started dates, sort by date
            if (aStarted && bStarted) {
              return aStarted - bStarted;
            }

            // If only one has started date, put started one first
            if (aStarted && !bStarted) return -1;
            if (!aStarted && bStarted) return 1;

            // If neither has started date, sort by order
            return (a.order || 0) - (b.order || 0);
          });

        // Add child measurements
        childMeasurements.forEach((measurement) => {
          hierarchicalPhases.push({
            ...measurement,
            isChild: true, // Flag to indicate this is a child measurement
            parentMilestone: milestone,
          });
        });
      });

      // Add orphaned measurements (those with no parent) at the end
      const orphanedMeasurements = measurements
        .filter(
          (measurement) =>
            !measurement.parentGUID &&
            !hierarchicalPhases.some((phase) => phase._id === measurement._id)
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      orphanedMeasurements.forEach((measurement) => {
        hierarchicalPhases.push({
          ...measurement,
          isOrphaned: true, // Flag to indicate this is an orphaned measurement
        });
      });

      phases = hierarchicalPhases;

      // Ensure phases is always an array
      if (!Array.isArray(phases)) {
        console.warn('Phases data is not an array, initializing as empty array');
        phases = [];
      }
    } catch (err) {
      console.error('Error fetching subject activities:', err);
      // Initialize phases as empty array on error
      phases = [];
    } finally {
      isLoading = false;
    }
  }

  async function fetchPublishedSchedule() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/schedules/schedulesByStudy?studyGUID=${$selectedStudy._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch study schedules');
      }

      const response = await res.json();
      const schedules = Array.isArray(response.data) ? response.data : [];

      // Find the published schedule
      publishedSchedule = schedules.find((schedule) => schedule.status === 'Published');
    } catch (err) {
      console.error('Error fetching published schedule:', err);
    }
  }

  async function fetchMeasurementActivities() {
    if (!publishedSchedule) {
      console.error('No published schedule found');
      return;
    }

    try {
      isLoadingActivities = true;
      const res = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${publishedSchedule._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch activities');
      }

      const response = await res.json();
      const allActivities = Array.isArray(response.data) ? response.data : [];

      // Filter for on-demand MEASUREMENT activities only
      measurementActivities = allActivities.filter(
        (activity) => activity.activityTypeCode === 'MEAS' && activity.onDemand === true
      );
    } catch (err) {
      console.error('Error fetching measurement activities:', err);
      measurementActivities = [];
    } finally {
      isLoadingActivities = false;
    }
  }

  function openAddActivityModal() {
    if (!$selectedParticipant || !$selectedStudy) {
      generationMessage = 'Please select both a participant and a study';
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
      return;
    }

    if (!publishedSchedule) {
      generationMessage = 'No published schedule found for this study';
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
      return;
    }

    showAddActivityModal = true;
    fetchMeasurementActivities();
  }

  async function handleAddActivity(activity) {
    try {
      // Find the currently started milestone
      const startedMilestone = phases.find(
        (phase) => phase.typeCode === 'MILE' && phase.statusLabel === 'Started'
      );

      if (!startedMilestone) {
        generationMessage =
          'Error: No phase is currently in "Started" state. Please start a phase before adding measurement activities.';
        showGenerationMessage = true;
        setTimeout(() => {
          showGenerationMessage = false;
        }, 5000);
        return;
      }

      // Find the "Started" status GUID
      const startStatus = statusOptions.find(opt => opt.code === 'START');
      if (!startStatus) {
        generationMessage = 'Error: Could not find "Started" status';
        showGenerationMessage = true;
        setTimeout(() => {
          showGenerationMessage = false;
        }, 3000);
        return;
      }

      const newSubjectActivity = {
        name: activity.name,
        description: activity.description,
        parentGUID: startedMilestone._id, // Set parent to the started milestone
        subjectScheduleGUID: subjectSchedule?._id,
        activityGUID: activity._id,
        activityTypeGUID: activity.activityTypeGUID,
        planned: new Date(),
        started: new Date().toISOString(), // Set to current time since we're starting it
        completed: null,
        subjectDeviceGUID: null,
        statusGUID: startStatus.id, // Set to "Started" status
        subjectGUID: $selectedParticipant._id,
        deviceGUID: activity.deviceGUID,
        formGUID: activity.formGUID,
        onDemand: activity.onDemand || false,
        allowDateEntryIndicator: activity.allowDateEntryIndicator || false,
        enableAlerts: activity.enableAlerts || false,
        complianceWindowMin: activity.complianceWindowMin || 0,
        complianceWindowMax: activity.complianceWindowMax || 0,
        cgmBlinded: activity.cgmBlinded || false,
        smbgBlinded: activity.smbgBlinded || false,
        order: activity.order || 99,
        startTime: activity.startTime,
        gap: activity.gap || 0,
        windowMin: activity.windowMin || 0,
        windowMax: activity.windowMax || 0,
        readingDuration: activity.readingDuration,
        readingFrequency: activity.readingFrequency,
        readingCount: activity.readingCount || 0,
        code: activity.code,
        externalId: activity.externalId,
        readingDurationDisplayUnitGUID: activity.readingDurationDisplayUnitGUID,
        readingFrequencyDisplayUnitGUID: activity.readingFrequencyDisplayUnitGUID,
        complianceThreshold: activity.complianceThreshold || 0,
      };

      const res = await fetch(`${API_BASE_URL}/subjectSchedule/subject-activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSubjectActivity),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to create subject activity');
      }

      const data = await res.json();
      generationMessage = `Activity "${activity.name}" added successfully under milestone "${startedMilestone.name}"`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);

      // Close modal and refresh data
      showAddActivityModal = false;
      fetchUserPhases();
    } catch (err) {
      console.error('Error creating subject activity:', err);
      generationMessage = `Error: ${err.message}`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
    }
  }

  function openScheduleModal() {
    if (!$selectedParticipant || !$selectedStudy) {
      generationMessage = 'Please select both a participant and a study';
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
      return;
    }

    // Set the start date to today by default
    startDate = new Date();
    showDateModal = true;
  }

  function handleDateModalConfirm(event) {
    startDate = event.detail.selectedDate;
    generateSubjectSchedule();
  }

  async function generateSubjectSchedule() {
    try {
      isGenerating = true;
      generationMessage = 'Generating schedule...';
      showGenerationMessage = true;

      const res = await fetch(`${API_BASE_URL}/subjectSchedule/generate-subject-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subjectGUID: $selectedParticipant._id,
          studyGUID: $selectedStudy._id,
          startDate: startDate.toISOString(),
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to generate schedule');
      }

      const data = await res.json();
      generationMessage = `Schedule generated successfully with ${data.activities ? data.activities.length : 0} phases`;

      // Refresh the schedule list
      fetchSubjectSchedule();
      fetchUserPhases();
    } catch (err) {
      console.error('Error generating schedule:', err);
      generationMessage = `Error: ${err.message}`;
    } finally {
      isGenerating = false;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
    }
  }

  function handleDeleteClick(phase) {
    phaseToDelete = phase;
    showConfirmation = true;
  }

  function handleAuditLogClick(phase) {
    auditLogPhase = phase;
    showAuditLog = true;
  }

  function handlePhaseClick(phase) {
    //console.log('phase clicked:', phase);
    if (phase.typeCode === 'MILE') {
      selectedActivity = phase;
      showActivityEditor = true;
    }
  }

  function handleEditClick(phase) {
    selectedActivity = phase;
    showActivityEditor = true;
  }

  function handleNewClick() {}

  function toggleMeasurements() {
    showMeasurements = !showMeasurements;
  }

  function handleRefresh() {
    //console.log('handleRefresh');
    fetchSubjectSchedule();
    fetchUserPhases();
  }

  function handleActivitySaved() {
    fetchUserPhases();
  }

  function handleActivityEditorClose() {
    showActivityEditor = false;
    selectedActivity = null;
  }

  // Legacy parity: milestone visit phases (MILE) show date-only;
  // non-milestone sub-activities show full date+time. Without this, all
  // planned/started/completed cells leaked "12:00:00 AM" suffixes that
  // legacy never showed.
  function formatActivityProgressDate(phase, field) {
    if (!phase?.[field]) return '';
    const tz = $selectedParticipant?.timezoneCode;
    if (phase.typeCode === 'MILE') {
      return formatToLocalDate(phase[field], tz);
    }
    return formatToLocalDateTime(phase[field], tz);
  }

  function formatVisitDate(dateString) {
    if (!dateString) return '';

    try {
      // If it's already in MM/DD/YYYY format, return it as is
      if (dateString.includes('/')) return dateString;

      // Check if it's an ISO date string
      if (dateString.includes('T')) {
        // It's a full ISO datetime string, convert to Date and then format
        const date = new Date(dateString);
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      }

      // Handle YYYY-MM-DD format
      const parts = dateString.split('-');
      if (parts.length === 3) {
        // Reformat from YYYY-MM-DD to MM/DD/YYYY
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }

      // Fallback for any other format
      return dateString;
    } catch (error) {
      console.error('Error formatting visit date:', error);
      return dateString;
    }
  }

  async function fetchPublishedScheduleVersion() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/schedules/schedulesByStudy?studyGUID=${$selectedStudy._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch study schedules');
      }

      const response = await res.json();
      const schedules = Array.isArray(response.data) ? response.data : [];

      // Find the published schedule
      const published = schedules.find((schedule) => schedule.status === 'Published');
      if (published) {
        publishedScheduleVersion = published.scheduleVersion;
      }
    } catch (err) {
      console.error('Error fetching published schedule version:', err);
    }
  }

  async function loadStatusOptions() {
    try {
      const response = await fetch(`${API_BASE_URL}/lookups/SUBACTSTAT`);
      if (response.ok) {
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          statusOptions = data.data;
        }
      }
    } catch (error) {
      console.error('Error loading status options:', error);
    }
  }

  function openStartConfirmation(phase) {
    activityToStart = phase;
    showStartConfirmation = true;
  }

  async function startActivity(phase) {
    if (phase.typeCode !== 'MILE') {
      return;
    }

    try {
      // Find the "Started" status GUID
      const startStatus = statusOptions.find(opt => opt.code === 'START');
      if (!startStatus) {
        generationMessage = 'Error: Could not find "Started" status';
        showGenerationMessage = true;
        setTimeout(() => {
          showGenerationMessage = false;
        }, 3000);
        return;
      }

      // Complete all currently started milestone activities for this subject
      await completeAllStartedMilestones();

      // Then start the selected activity
      const updateData = {
        subjectActivityGUID: phase._id,
        timezone: $selectedParticipant.timezoneCode || 'UTC',
        updates: {
          statusGUID: startStatus.id,
          started: new Date().toISOString(),
          completed: null // clear completed if previously set
        }
      };

      const res = await fetch(`${API_BASE_URL}/subjectSchedule/update-subject-activity`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to start activity');
      }

      generationMessage = `Activity "${phase.name}" started successfully`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);

      // Refresh the data
      fetchUserPhases();
    } catch (err) {
      console.error('Error starting activity:', err);
      generationMessage = `Error: ${err.message}`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
    }
  }

  function openStartMeasurementConfirmation(phase) {
    measurementActivityToStart = phase;
    showStartMeasurementConfirmation = true;
  }

  async function startMeasurementActivity(phase) {
    if (phase.typeCode !== 'MEAS') {
      return;
    }

    try {
      // Find the "Started" status GUID
      const startStatus = statusOptions.find(opt => opt.code === 'START');
      if (!startStatus) {
        generationMessage = 'Error: Could not find "Started" status';
        showGenerationMessage = true;
        setTimeout(() => {
          showGenerationMessage = false;
        }, 3000);
        return;
      }

      // Start the measurement activity
      const updateData = {
        subjectActivityGUID: phase._id,
        timezone: $selectedParticipant.timezoneCode || 'UTC',
        updates: {
          statusGUID: startStatus.id,
          started: new Date().toISOString(),
          completed: null // clear completed if previously set
        }
      };

      const res = await fetch(`${API_BASE_URL}/subjectSchedule/update-subject-activity`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to start measurement activity');
      }

      generationMessage = `Measurement activity "${phase.name}" started successfully`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);

      // Refresh the data
      fetchUserPhases();
    } catch (err) {
      console.error('Error starting measurement activity:', err);
      generationMessage = `Error: ${err.message}`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
    }
  }

  async function completeAllStartedMilestones() {
    try {
      // Find the "Completed" status GUID
      const completeStatus = statusOptions.find(opt => opt.code === 'COMP');
      if (!completeStatus) {
        console.warn('Could not find "Completed" status');
        return;
      }

      // Find all started milestone activities for this subject
      const startedMilestones = phases.filter(
        phase => 
          phase.typeCode === 'MILE' && 
          phase.statusLabel === 'Started' &&
          phase.subjectGUID === $selectedParticipant._id
      );

      // Complete each started milestone
      for (const milestone of startedMilestones) {
        const updateData = {
          subjectActivityGUID: milestone._id,
          timezone: $selectedParticipant.timezoneCode || 'UTC',
          updates: {
            statusGUID: completeStatus.id,
            completed: new Date().toISOString()
          }
        };

        await fetch(`${API_BASE_URL}/subjectSchedule/update-subject-activity`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });
      }
    } catch (error) {
      console.error('Error completing started milestones:', error);
    }
  }

  function openCompleteConfirmation(phase) {
    activityToComplete = phase;
    showCompleteConfirmation = true;
  }

  async function completeActivity(phase) {
    if (!['MILE', 'MEAS'].includes(phase.typeCode)) {
      return;
    }

    try {
      // Find the "Completed" status GUID
      const completeStatus = statusOptions.find(opt => opt.code === 'COMP');
      if (!completeStatus) {
        generationMessage = 'Error: Could not find "Completed" status';
        showGenerationMessage = true;
        setTimeout(() => {
          showGenerationMessage = false;
        }, 3000);
        return;
      }

      const updateData = {
        subjectActivityGUID: phase._id,
        timezone: $selectedParticipant.timezoneCode || 'UTC',
        updates: {
          statusGUID: completeStatus.id,
          completed: new Date().toISOString()
        }
      };

      const res = await fetch(`${API_BASE_URL}/subjectSchedule/update-subject-activity`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to complete activity');
      }

      generationMessage = `${
        phase.typeCode === 'MEAS' ? 'Measurement activity' : 'Activity'
      } "${phase.name}" completed successfully`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);

      // Refresh the data
      fetchUserPhases();
    } catch (err) {
      console.error('Error completing activity:', err);
      generationMessage = `Error: ${err.message}`;
      showGenerationMessage = true;
      setTimeout(() => {
        showGenerationMessage = false;
      }, 3000);
    }
  }

  function handleImportClick(phase) {
    currentPhaseForImport = phase;
    fileInput.click();
  }

  async function handleFileSelected(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      // Read the file
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      // Validate the JSON structure
      if (!jsonData.metadata || !jsonData.readings || !Array.isArray(jsonData.readings)) {
        alert('Invalid JSON file format. Expected file with "metadata" and "readings" properties.');
        return;
      }

      // Confirm import
      const confirmed = confirm(
        `Import glucose readings?\n\n` +
        `File contains ${jsonData.readings.length} readings\n` +
        `Activity: ${currentPhaseForImport.name}\n\n` +
        `Duplicates will be skipped automatically.`
      );

      if (!confirmed) {
        return;
      }

      // Show loading state
      isImporting = true;
      importProgress = `Importing ${jsonData.readings.length} readings...`;

      // Prepare import request
      const importData = {
        subjectGUID: $selectedParticipant._id,
        activityGUID: currentPhaseForImport._id,
        protocolNumber: $selectedStudy.protocolNumber,
        readings: jsonData.readings
      };

      // Call import API
      const response = await fetch(`${API_BASE_URL}/stats/importGlucoseReadings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(importData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import glucose readings');
      }

      const result = await response.json();

      // Build result message
      let resultMessage = `Import completed!\n\n` +
        `Total provided: ${result.summary.totalProvided}\n` +
        `Duplicates skipped: ${result.summary.duplicatesSkipped}\n` +
        `Records inserted: ${result.summary.recordsInserted}`;

      if (result.summary.errorCount > 0) {
        resultMessage += `\nErrors: ${result.summary.errorCount}`;
        if (result.errors && result.errors.length > 0) {
          resultMessage += `\n\nFirst errors (check console for full details):\n`;
          result.errors.slice(0, 5).forEach(err => {
            if (err.message) {
              resultMessage += `- ${err.message}\n`;
            } else {
              // Try to parse and show more readable error
              let errorText = err.error;
              try {
                const parsed = JSON.parse(err.error);
                if (parsed.details) {
                  errorText = JSON.stringify(parsed.details, null, 2);
                }
              } catch (e) {
                // Keep original error text
              }
              resultMessage += `- Index ${err.index}: ${errorText.substring(0, 200)}\n`;
            }
          });
        }
        
        // Log full errors to console for debugging
        console.error('Import errors:', result.errors);
      }

      // Auto-complete the MEAS activity after successful import
      let activityCompleted = false;
      let completionError = null;

      try {
        // Find the "Completed" status GUID
        const completeStatus = statusOptions.find(opt => opt.code === 'COMP');
        if (completeStatus) {
          const updateData = {
            subjectActivityGUID: currentPhaseForImport._id,
            timezone: $selectedParticipant.timezoneCode || 'UTC',
            updates: {
              statusGUID: completeStatus.id,
              completed: new Date().toISOString()
            }
          };

          const completeResponse = await fetch(`${API_BASE_URL}/subjectSchedule/update-subject-activity`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData),
          });

          if (completeResponse.ok) {
            activityCompleted = true;
            resultMessage += `\n\nActivity "${currentPhaseForImport.name}" marked as completed.`;
          } else {
            const errorData = await completeResponse.json();
            completionError = errorData.message || 'Unknown error';
          }
        } else {
          completionError = 'Completed status not found';
        }
      } catch (err) {
        console.error('Error auto-completing activity:', err);
        completionError = err.message;
      }

      // Add note if completion failed
      if (!activityCompleted && completionError) {
        resultMessage += `\n\nNote: Could not auto-complete activity (${completionError}). Please complete it manually if needed.`;
      }

      // Show result message
      alert(resultMessage);

      // Refresh the schedule data
      fetchUserPhases();
    } catch (error) {
      console.error('Error importing glucose readings:', error);
      alert(`Failed to import glucose readings: ${error.message}`);
    } finally {
      // Reset state
      isImporting = false;
      importProgress = '';
      event.target.value = '';
      currentPhaseForImport = null;
    }
  }
</script>

<StudyHeader />

<FeatureInfo
  title="Participant Schedule"
  description="This page shows the schedule of activities for the selected participant. You can generate a new schedule, view and edit milestone activities, and track their status. The schedule is based on the study's published schedule template."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails 
    showOptionsMenu={false}
  />
{/if}

<!-- Hidden file input for importing glucose readings -->
<input
  type="file"
  accept=".json"
  bind:this={fileInput}
  on:change={handleFileSelected}
  style="display: none;"
/>

<!-- Import progress indicator -->
{#if isImporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-xl max-w-md">
      <div class="flex flex-col items-center">
        <div class="loader mb-4"></div>
        <p class="text-lg font-semibold text-gray-800 dark:text-white mb-2">Importing Glucose Readings</p>
        <p class="text-sm text-gray-600 dark:text-gray-300 text-center">{importProgress}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">Please wait, this may take a few moments...</p>
      </div>
    </div>
  </div>
{/if}

  <Toolbar
    title="{subjectSchedule ? subjectSchedule.name : 'No schedule found'} - Version: {subjectSchedule
      ? subjectSchedule.scheduleVersion : ''}{publishedScheduleVersion && subjectSchedule && publishedScheduleVersion !== subjectSchedule.scheduleVersion
      ? ' (Newest: ' + publishedScheduleVersion + ')' : ''}"
    itemCountTitle="Entries"
  itemCount={phases.length}
>
  <div class="flex items-center gap-2">
    {#if canAddMeasurementActivity}
      <button
        class="pagination mx-1 p-0"
        on:click={openAddActivityModal}
        disabled={!publishedSchedule || !hasStartedMilestones}
        class:opacity-50={!publishedSchedule || !hasStartedMilestones}
        class:cursor-not-allowed={!publishedSchedule || !hasStartedMilestones}
        title={!publishedSchedule ? 'No published schedule available' : !hasStartedMilestones ? 'No milestone in "Started" state. Please start a milestone first.' : 'Add Measurement Activity'}
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>
    {/if}
    <button
      class="pagination mx-1 p-0"
      on:click={openScheduleModal}
      disabled={isGenerating || hasStartedMilestones}
      class:opacity-50={hasStartedMilestones}
      class:cursor-not-allowed={hasStartedMilestones}
      title={hasStartedMilestones ? 'Cannot generate schedule while milestone activities are in progress' : 'Generate Schedule'}
    >
      <FontAwesomeIcon icon={faCalendarPlus} />
    </button>
    {#if isSMBGEnabled}
      <button
        class="pagination mx-1 p-0"
        on:click={toggleMeasurements}
        title="Show/Hide SMBG"
      >
        {#if showMeasurements}
          <FontAwesomeIcon icon={faEye} />
        {:else}
          <FontAwesomeIcon icon={faEyeSlash} />
        {/if}
      </button>
    {/if}
    <button class="pagination mx-1 p-0" on:click={handleRefresh} title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if showGenerationMessage}
  <div class="message-banner {generationMessage.includes('Error') ? 'error' : 'success'}">
    {generationMessage}
  </div>
{/if}

<!-- Add Activity Modal -->
{#if showAddActivityModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div
      class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Add Measurement Activity
        </h2>
        <button
          on:click={() => (showAddActivityModal = false)}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} class="w-5 h-5" />
        </button>
      </div>

      {#if isLoadingActivities}
        <div class="flex flex-col justify-center items-center h-32">
          <div class="loader mb-4"></div>
          <p class="text-sm text-gray-700 dark:text-gray-300">Loading measurement activities...</p>
        </div>
      {:else if measurementActivities.length === 0}
        <div class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No measurement activities found in the published schedule.
          </p>
        </div>
      {:else}
        <div class="space-y-2">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Select a measurement activity from the published schedule to add to this participant's
            schedule:
          </p>
          {#each measurementActivities as activity}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              on:click={() => handleAddActivity(activity)}
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="font-medium text-gray-900 dark:text-white">{activity.name}</h3>
                  {#if activity.description}
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {activity.description}
                    </p>
                  {/if}
                  <div
                    class="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400"
                  >
                    {#if activity.deviceName}
                      <span>Device: {activity.deviceName}</span>
                    {/if}
                    {#if activity.readingDuration}
                      <span
                        >Duration: {Math.round((activity.readingDuration / 86400) * 10) / 10} days</span
                      >
                    {/if}
                    {#if activity.readingCount}
                      <span>Readings: {activity.readingCount}</span>
                    {/if}
                  </div>
                </div>
                <FontAwesomeIcon icon={faMicrochip} class="text-sky-800 dark:text-white/70 ml-4" />
              </div>
            </div>
          {/each}
        </div>
      {/if}

      <div class="flex justify-end mt-6">
        <button
          on:click={() => (showAddActivityModal = false)}
          class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<DateSelectModal
  bind:show={showDateModal}
  bind:selectedDate={startDate}
  title="Select Start Date"
  message="Please select the starting date for the first phase. This date will be used as the local date in the subject's timezone. Subsequent phase dates will be calculated based on the durations configured in the schedule."
  confirmButtonText="Generate Schedule"
  on:confirm={handleDateModalConfirm}
/>

<AuditLogModal
  showModal={showAuditLog}
  documentId={auditLogPhase?._id}
  collection="SubjectActivity"
  title={`Audit Logs - ${auditLogPhase?.name || 'Phase'}`}
  on:close={() => (showAuditLog = false)}
/>

<SubjectActivityEditor
  bind:show={showActivityEditor}
  subjectActivity={selectedActivity}
  timezone={$selectedParticipant?.timezoneCode || 'UTC'}
  on:saved={handleActivitySaved}
  on:close={handleActivityEditorClose}
/>

<ConfirmationModal
  showConfirmation={showStartConfirmation}
  message="Are you sure you want to start the phase '{activityToStart?.name}'? This will complete any running phases and begin this new one. Running sensors will not be stopped."
  confirmPhrase="start"
  on:close={() => {
    showStartConfirmation = false;
    activityToStart = null;
  }}
  on:confirm={() => {
    if (activityToStart) {
      startActivity(activityToStart);
    }
    showStartConfirmation = false;
    activityToStart = null;
  }}
/>

<ConfirmationModal
  showConfirmation={showCompleteConfirmation}
  message={activityToComplete?.typeCode === 'MEAS'
    ? `Are you sure you want to complete the measurement activity '${activityToComplete?.name}'?`
    : `Are you sure you want to complete the phase '${activityToComplete?.name}'? Running sensors will not be stopped.`}
  confirmPhrase="complete"
  on:close={() => {
    showCompleteConfirmation = false;
    activityToComplete = null;
  }}
  on:confirm={() => {
    if (activityToComplete) {
      completeActivity(activityToComplete);
    }
    showCompleteConfirmation = false;
    activityToComplete = null;
  }}
/>

<ConfirmationModal
  showConfirmation={showStartMeasurementConfirmation}
  message="Are you sure you want to start the measurement activity '{measurementActivityToStart?.name}'?"
  confirmPhrase="start"
  on:close={() => {
    showStartMeasurementConfirmation = false;
    measurementActivityToStart = null;
  }}
  on:confirm={() => {
    if (measurementActivityToStart) {
      startMeasurementActivity(measurementActivityToStart);
    }
    showStartMeasurementConfirmation = false;
    measurementActivityToStart = null;
  }}
/>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">preparing schedule data</p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="pl-2 pr-0 py-2 text-center bold"></th>
          <th class="px-2 py-2 text-left bold">Name</th>
          <!-- <th class="px-4 py-2 text-left bold w-32">Visit Date</th> -->
          <th class="px-4 py-2 text-left bold">Planned (Local)</th>
          <th class="px-4 py-2 text-left bold">Started (Local)</th>
          <th class="px-4 py-2 text-left bold">Duration</th>
          <th class="px-4 py-2 text-left bold">Completed (Local)</th>
          <th class="px-4 py-2 text-left bold">Blinded</th>
          <th class="w-32 px-4 py-2 text-left bold">Status</th>
          <th class="w-10 px-4 py-2 text-left bold"></th>
          <th class="w-24 px-4 py-2 text-left bold">Actions</th>
        </tr>
      </thead>

      <tbody>
        {#each phases as phase, index (phase._id || index)}
          {@const isVisible =
            phase.typeCode !== 'MEAS' ||
            phase.readingCount > 1 ||
            showMeasurements}
          {#if isVisible}
            {@const rowIndex = phases
              .slice(0, index)
              .filter(
                (p) =>
                  p.typeCode !== 'MEAS' ||
                  p.readingCount > 1 ||
                  showMeasurements
              ).length}
            <tr
              class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
                2 ===
              0
                ? 'bg-stone-50 dark:bg-gray-725'
                : 'bg-stone-75 dark:bg-gray-750'}"
            >
              <td class="pl-2 pr-0 py-2 text-center" on:click={() => handlePhaseClick(phase)}>
                {#if phase.typeCode === 'MILE'}
                  <FontAwesomeIcon
                    icon={faTimer}
                    class="text-sky-800 dark:text-white/70"
                    title="Phase"
                  />
                {/if}
              </td>
              <td
                class="px-2 py-2"
                on:click={() => handlePhaseClick(phase)}
                class:pl-2={phase.isChild}
                class:pl-0={phase.isOrphaned}
              >
                {#if phase.typeCode === 'MEAS'}
                  <FontAwesomeIcon
                    icon={faMicrochip}
                    class="text-sky-800 dark:text-white/70 mr-2"
                    title="Device"
                  />
                {/if}
                {phase.name}
              </td>
              <!-- <td class="px-4 py-2 w-32" on:click={() => handlePhaseClick(phase)}
                >{phase.visitDate
                  ? typeof phase.visitDate === 'string' && !phase.visitDate.includes('T')
                    ? formatVisitDate(phase.visitDate)
                    : formatToLocalDate(phase.visitDate, $selectedParticipant.timezoneCode)
                  : ''}</td
              > -->
              <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}
                >{phase.typeCode === 'MILE' && phase.planned
                  ? formatToLocalDate(phase.planned, $selectedParticipant.timezoneCode)
                  : ''}</td
              >
              <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}
                >{formatActivityProgressDate(phase, 'started')}</td
              >
              <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}
                >{phase.typeCode === 'MILE' && phase.gap
                  ? Math.round((phase.gap / 86400) * 10) / 10
                  : ''}</td
              >
              <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}
                >{formatActivityProgressDate(phase, 'completed')}</td
              >
              <td class="px-4 py-2" on:click={() => handlePhaseClick(phase)}
                >{phase.typeCode === 'MILE' ? (phase.cgmBlinded ? 'Yes' : 'No') : ''}</td
              >
              <td class="px-4 py-2">
                <span
                  class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                  class:text-green-700={phase.statusLabel === 'Completed'}
                  class:ring-green-700={phase.statusLabel === 'Completed'}
                  class:text-slate-600={phase.statusLabel === 'Cancelled'}
                  class:ring-slate-600={phase.statusLabel === 'Cancelled'}
                  class:text-green-600={phase.statusLabel === 'Started'}
                  class:ring-green-600={phase.statusLabel === 'Started'}
                  class:text-sky-600={phase.statusLabel === 'Scheduled'}
                  class:ring-sky-600={phase.statusLabel === 'Scheduled'}
                >
                  {phase.statusLabel}
                </span>
              </td>
              <td class="px-4 py-2">
                {#if phase.typeCode === 'MILE'}
                  {#if phase.statusLabel === 'Not Started'}
                    {#if (() => {
                      // Only show for the first not started milestone
                      let found = false;
                      for (let p of phases) {
                        if (p.typeCode === 'MILE' && p.statusLabel === 'Not Started') {
                          if (!found && p._id === phase._id) return true;
                          if (!found) found = true;
                          if (found && p._id !== phase._id) return false;
                        }
                      }
                      return false;
                    })()}
                      <button
                        on:click={() => openStartConfirmation(phase)}
                        class="pagination mx-1 p-0"
                        title="Start Phase"
                        disabled={!canStartStopSchedule}
                        class:opacity-50={!canStartStopSchedule}
                      >
                        <FontAwesomeIcon icon={faPlay} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {/if}
                  {:else if phase.statusLabel === 'Started'}
                    <button
                      on:click={() => openCompleteConfirmation(phase)}
                      class="pagination mx-1 p-0"
                      title="Complete Phase"
                      disabled={!canStartStopSchedule}
                      class:opacity-50={!canStartStopSchedule}
                    >
                      <FontAwesomeIcon icon={faStop} class="text-sky-800 dark:text-white/70" />
                    </button>
                  {/if}
                {:else if phase.typeCode === 'MEAS' && phase.statusLabel === 'Started'}
                  <button
                    on:click={() => openCompleteConfirmation(phase)}
                    class="pagination mx-1 p-0"
                    title="Complete Measurement Activity"
                    disabled={!canCompleteMeasurements}
                    class:opacity-50={!canCompleteMeasurements}
                  >
                    <FontAwesomeIcon icon={faStop} class="text-sky-800 dark:text-white/70" />
                  </button>
                {/if}
              </td>
              <td class="px-4 py-2">
                {#if phase.typeCode === 'MEAS'}
                  {#if canUseMeasurementAdminTools}
                    {#if phase.statusLabel === 'Not Started'}
                      <button
                        on:click={() => openStartMeasurementConfirmation(phase)}
                        class="pagination mx-1 p-0"
                        title="Start Measurement Activity"
                      >
                        <FontAwesomeIcon icon={faPlay} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {/if}
                    <button
                      on:click={() => handleImportClick(phase)}
                      class="pagination mx-1 p-0"
                      title="Import glucose readings from JSON"
                      disabled={!canUseMeasurementAdminTools}
                      class:opacity-50={!canUseMeasurementAdminTools}
                    >
                      <FontAwesomeIcon icon={faFileImport} class="text-sky-800 dark:text-white/70" />
                    </button>
                  {/if}
                {:else}
                  <button
                    on:click={() => handleEditClick(phase)}
                    class="pagination mx-1 p-0"
                    title="Edit"
                    disabled={!canEditSchedule}
                    class:opacity-50={!canEditSchedule}
                  >
                    <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                  </button>
                  <button
                    on:click={() => handleAuditLogClick(phase)}
                    class="pagination mx-1 p-0"
                    title="View audit logs"
                    disabled={!canViewAuditLogs}
                    class:opacity-50={!canViewAuditLogs}
                  >
                    <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                  </button>
                {/if}
              </td>
            </tr>
          {/if}
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Footer />

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .message-banner {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 10px 20px;
    border-radius: 5px;
    z-index: 1000;
    animation: fadeInOut 3s;
  }

  .success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  @keyframes fadeInOut {
    0% {
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
</style>
